import { useTranslation } from 'react-i18next'

import { Box, Flex, Stack, Text } from '@hivewatch/dark'

import type {
  SettingsSectionDefinition,
  SettingsSectionId,
} from '../types/types'
import { settingsColorToken } from '../utils/settingsColorToken'
import { SettingsSectionActions } from './SettingsSectionActions'

interface SettingsShellIProps {
  activeSection: SettingsSectionDefinition
  colorScheme: 'light' | 'dark'
  onSelectSection: (sectionId: SettingsSectionId) => void
  sections: SettingsSectionDefinition[]
}

export const SettingsShell = ({
  activeSection,
  colorScheme,
  onSelectSection,
  sections,
}: SettingsShellIProps) => {
  const { t } = useTranslation()
  const visibleContent = activeSection.items.filter(
    (item) =>
      item.isVisible && item.content !== undefined && item.content !== null
  )

  const appBackground = settingsColorToken(colorScheme, 'bg.app')
  const cardBackground = settingsColorToken(colorScheme, 'bg.card')
  const hoverBackground = settingsColorToken(colorScheme, 'bg.table-row-hover')
  const primaryText = settingsColorToken(colorScheme, 'text.primary')
  const secondaryText = settingsColorToken(colorScheme, 'text.secondary')
  const subtleBorder = settingsColorToken(colorScheme, 'border.subtle')
  const focusRing = settingsColorToken(colorScheme, 'interactive.focus-ring')

  const getSectionContent = () => {
    switch (activeSection.contentState ?? 'content') {
      case 'loading':
        return activeSection.loadingState
      case 'empty':
        return activeSection.emptyState
      case 'error':
        return activeSection.errorState
      case 'read-only':
        return activeSection.readOnlyState
      case 'content':
        return visibleContent.length > 0 ? (
          <Stack gap='40px' mt='24px'>
            {visibleContent.map((item) => (
              <Box key={item.id}>{item.content}</Box>
            ))}
          </Stack>
        ) : null
    }
  }

  return (
    <Flex h='100vh' minH='0' overflow='hidden'>
      <Box
        as='aside'
        bg={cardBackground}
        borderColor={subtleBorder}
        borderRightWidth='1px'
        flexShrink='0'
        overflowY='auto'
        px='42px'
        py='85px'
        w='337px'
      >
        <Text as='h1' color={primaryText} textStyle='display-extended'>
          {t('settings:rebrand_page_title')}
        </Text>
        <Stack
          aria-label={t('settings:rebrand_navigation_aria')}
          as='nav'
          gap='8px'
          mt='44px'
        >
          {sections.map((section) => {
            const isActive = section.id === activeSection.id

            return (
              <Box
                _focusVisible={{
                  outline: '2px solid',
                  outlineColor: focusRing,
                  outlineOffset: '2px',
                }}
                _hover={{ bg: hoverBackground, color: primaryText }}
                aria-current={isActive ? 'page' : undefined}
                as='button'
                bg='transparent'
                border='0'
                color={isActive ? primaryText : secondaryText}
                cursor='pointer'
                h='44px'
                key={section.id}
                onClick={() => onSelectSection(section.id)}
                px='12px'
                py='6px'
                textAlign='left'
                textStyle='body-large'
                w='full'
              >
                {t(section.labelKey)}
              </Box>
            )
          })}
        </Stack>
      </Box>

      <Box
        as='main'
        bg={appBackground}
        flex='1'
        minW='0'
        overflowY='auto'
        px='32px'
        py='85px'
      >
        <Flex alignItems='flex-start' justifyContent='space-between'>
          <Stack gap='4px'>
            <Text as='h2' color={primaryText} textStyle='heading-l'>
              {t(activeSection.titleKey)}
            </Text>
            <Text color={secondaryText} textStyle='body'>
              {t(activeSection.descriptionKey)}
            </Text>
          </Stack>

          <SettingsSectionActions
            colorScheme={colorScheme}
            section={activeSection}
          />
        </Flex>

        {getSectionContent()}
      </Box>
    </Flex>
  )
}
